import {
  faExclamationCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Box from '~/common/components/Box';
import Button from '~/common/components/Buttons/Button';
import Center from '~/common/components/Center';
import Flex from '~/common/components/Flex';
import Modal from '~/common/components/Modals/Modal';
import ModalBody from '~/common/components/Modals/ModalBody';
import ModalCloseButton from '~/common/components/Modals/ModalCloseButton';
import ModalContent from '~/common/components/Modals/ModalContent';
import ModalFooter from '~/common/components/Modals/ModalFooter';
import ModalOverlay from '~/common/components/Modals/ModalOverlay';
import Text from '~/common/components/Texts/Text';
import { ALERT_SEVERITY } from '~/common/constants';

import lang from './Modal/language.json';

type ModalIconProps = {
  severity?: number;
};

function ModalIcon({ severity }: ModalIconProps) {
  const commonStyle = { marginBottom: '8px', fontSize: '32px' };

  switch (severity) {
    case ALERT_SEVERITY.WARNING:
      return (
        <Box
          as="span"
          sx={commonStyle}
          color="orange.300"
          id="bailout-warning-icon"
        >
          <FontAwesomeIcon icon={faExclamationTriangle} />
        </Box>
      );
    case ALERT_SEVERITY.ALERT:
      return (
        <Box as="span" sx={commonStyle} color="red.500" id="bailout-error-icon">
          <FontAwesomeIcon icon={faExclamationCircle} />
        </Box>
      );
    default:
      return null;
  }
}

type ModalComponentProps = {
  isOpen: boolean;
  onClose: (...args: any[]) => any;
  children?: any;
  footer: React.ReactNode;
};

function ModalComponent({
  isOpen,
  onClose,
  children,
  footer,
}: Readonly<ModalComponentProps>) {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent marginTop={350}>
        <ModalCloseButton />
        <ModalBody mt="24px">{children}</ModalBody>
        <ModalFooter display="flex" justifyContent="center" pb="2rem">
          <Flex justifyContent="space-between" gridGap="12px">
            {footer}
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

type AlertOkModalProps = {
  isOpen: boolean;
  handleClose: (...args: any[]) => any;
  handleOk?: (...args: any[]) => any;
  loading?: boolean;
  data?: {
    severity?: number;
    title?: string;
    description?: string;
  };
};

export default function AlertOkModal({
  isOpen,
  handleClose,
  handleOk,
  loading,
  data,
}: Readonly<AlertOkModalProps>) {
  if (!data) {
    return null;
  }

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <Button
          backgroundColor="blue.600"
          color="white"
          _hover={{
            backgroundColor: 'blue.500',
          }}
          onClick={handleOk || handleClose}
          isLoading={loading}
        >
          {lang.OK_BUTTON}
        </Button>
      }
    >
      <Center>
        <ModalIcon severity={data.severity} />
      </Center>
      <Text as="h3" textAlign="center" mb="16px">
        {data.title}
      </Text>
      <Text as="p" fontSize="14px" textAlign="center" lineHeight="1.5rem">
        {data.description}
      </Text>
    </ModalComponent>
  );
}
